import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { supabase } from "@/integrations/supabase/client";
import { normalizeAuthEmail } from "@/lib/authEmail";

export const Route = createFileRoute("/signup")({
  head: () => ({
    meta: [
      { title: "Create Your Library Account — Currency Thoughts" },
      {
        name: "description",
        content:
          "Create a Currency Thoughts account to purchase and download workbooks from the study library.",
      },
      { name: "robots", content: "noindex" },
      {
        property: "og:title",
        content: "Create Your Library Account — Currency Thoughts",
      },
      {
        property: "og:description",
        content:
          "Create an account to purchase and download Currency Thoughts study materials.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "/signup" }],
  }),
  component: SignupPage,
});

function safeRedirectParam(): string | null {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("redirect");
  return raw && raw.startsWith("/") && !raw.startsWith("//") ? raw : null;
}

function SignupPage() {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Use at least 8 characters for your password.");
      return;
    }
    setLoading(true);
    const dest = safeRedirectParam() ?? "/store/library";
    const { data, error } = await supabase.auth.signUp({
      email: normalizeAuthEmail(email),
      password,
      options: {
        emailRedirectTo: `${window.location.origin}${dest}`,
        data: { full_name: fullName.trim() },
      },
    });
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    if (data.session) {
      window.location.assign(dest);
      return;
    }
    setSent(true);
  };

  return (
    <AuthLayout eyebrow="Study Library" heading="Create your account">
      {sent ? (
        <div className="space-y-4 font-sans text-[14px] leading-relaxed text-charcoal-900">
          <p>
            Check your inbox to confirm your email address. Once confirmed,
            you'll be returned here to complete your purchase.
          </p>
          <Link
            to="/login"
            className="inline-block font-semibold text-bronze-600 hover:text-bronze-400"
          >
            Already confirmed? Sign in →
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-5">
          <Field
            id="fullName"
            label="Full name"
            type="text"
            value={fullName}
            onChange={setFullName}
            autoComplete="name"
            required
          />
          <Field
            id="email"
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="email"
            required
          />
          <Field
            id="password"
            label="Password"
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            required
          />
          <PrimaryButton
            type="submit"
            size="lg"
            disabled={loading}
            className="w-full"
          >
            {loading ? "Creating account…" : "Create account"}
          </PrimaryButton>
          <p className="text-center font-sans text-[12px] leading-relaxed text-charcoal-500">
            This account holds your purchased study materials. Already have
            one?{" "}
            <Link
              to="/login"
              className="font-semibold text-bronze-600 hover:text-bronze-400"
            >
              Sign in
            </Link>
            .
          </p>
        </form>
      )}
    </AuthLayout>
  );
}

function Field({
  id,
  label,
  type,
  value,
  onChange,
  autoComplete,
  required,
}: {
  id: string;
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-charcoal-500"
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        required={required}
        className="mt-2 w-full border border-bronze-600/40 bg-cream-50 px-4 py-3 font-sans text-[14px] text-charcoal-900 focus:border-bronze-600 focus:outline-none"
      />
    </div>
  );
}
